// Join Competition screen
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Button from '../components/Button';
import Input from '../components/Input';
import Card from '../components/Card';
import {
  getCompetitionByInviteCode,
  findPlayerByDisplayName,
  createPlayer,
  verifyPlayerPin,
  countActivePlayers
} from '../services';
import { createPlayerToken, checkForOrphanedTokens, migrateSpecificTokens } from '../services/tokenService';
import { storeToken } from '../utils/localStorage';
import { validateDisplayName } from '../utils/displayName';
import { generatePin } from '../utils/crypto';
import { normalizeInviteCode } from '../utils/inviteCode';

const JoinCompetition = () => {
  const navigate = useNavigate();
  const { authUid } = useAuth();

  const [step, setStep] = useState('invite'); // 'invite', 'name', 'pin', 'show-pin'
  const [inviteCode, setInviteCode] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [pin, setPin] = useState('');
  const [generatedPin, setGeneratedPin] = useState(null);
  const [competition, setCompetition] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [existingPlayer, setExistingPlayer] = useState(null);

  const handleInviteCodeSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      console.log('Auth UID:', authUid);
      console.log('Looking up invite code:', inviteCode);

      const normalizedCode = normalizeInviteCode(inviteCode);
      console.log('Normalized code:', normalizedCode);

      const comp = await getCompetitionByInviteCode(normalizedCode);
      console.log('Competition found:', comp);

      if (!comp) {
        setError('Invalid invite code. Please check the code and try again.');
        setLoading(false);
        return;
      }

      setCompetition(comp);
      setStep('name');
      setLoading(false);
    } catch (err) {
      console.error('Error in handleInviteCodeSubmit:', err);
      console.error('Error details:', {
        code: err.code,
        message: err.message,
        stack: err.stack
      });

      // Show detailed error message
      if (err.code === 'permission-denied') {
        setError('Permission denied. Please make sure you are logged in.');
      } else if (err.message) {
        setError(`Error: ${err.message}`);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      setLoading(false);
    }
  };

  const handleDisplayNameSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const validationError = validateDisplayName(displayName);
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);

    try {
      const player = await findPlayerByDisplayName(competition.id, displayName);

      if (player) {
        // Existing player - check if they have orphaned tokens from a different authUid
        console.log('Checking for orphaned tokens for player:', player.id);
        const orphanedCheck = await checkForOrphanedTokens(competition.id, player.id, authUid);

        if (orphanedCheck.needsMigration) {
          // User has tokens with a different authUid - automatically migrate them
          console.log(`Found ${orphanedCheck.tokens.length} orphaned tokens, migrating...`);
          const tokenIds = orphanedCheck.tokens.map(t => t.id);
          await migrateSpecificTokens(tokenIds, authUid);

          // Store the first migrated token ID in localStorage
          storeToken(competition.id, tokenIds[0]);

          console.log('Token migration complete, redirecting to competition');
          // Redirect directly to competition
          navigate(`/competition/${competition.id}`);
          return;
        }

        // No orphaned tokens - need PIN to link
        setExistingPlayer(player);
        setStep('pin');
      } else {
        // New player - check max players
        const playerCount = await countActivePlayers(competition.id);
        if (playerCount >= competition.maxPlayers) {
          setError('Competition is full');
          setLoading(false);
          return;
        }

        // Generate PIN for new player
        const newPin = generatePin(6);
        const newPlayer = await createPlayer(competition.id, displayName, newPin);

        // Create token
        const tokenId = await createPlayerToken(competition.id, newPlayer.id, authUid);
        storeToken(competition.id, tokenId);

        setGeneratedPin(newPin);
        setStep('show-pin');
      }

      setLoading(false);
    } catch (err) {
      console.error('Error in handleDisplayNameSubmit:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const handlePinSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const isValid = await verifyPlayerPin(existingPlayer.id, pin);

      if (!isValid) {
        setError('Incorrect PIN');
        setLoading(false);
        return;
      }

      // Create token for existing player
      const tokenId = await createPlayerToken(competition.id, existingPlayer.id, authUid);
      storeToken(competition.id, tokenId);

      // Navigate to competition
      navigate(`/competition/${competition.id}`);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const handleContinueToCompetition = () => {
    // Redirect to account creation with competition ID
    navigate('/create-account', { state: { competitionId: competition.id } });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Competition Tracker</h1>
          <p className="mt-2 text-gray-600">Join a competition to get started</p>
        </div>

        <Card>
          {step === 'invite' && (
            <form onSubmit={handleInviteCodeSubmit}>
              <Input
                label="Invite Code"
                value={inviteCode}
                onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
                placeholder="Enter 6-character code"
                required
                maxLength={6}
                error={error}
              />
              <Button type="submit" fullWidth loading={loading} disabled={loading}>
                Continue
              </Button>
            </form>
          )}

          {step === 'name' && (
            <div>
              <h2 className="text-xl font-semibold mb-4">{competition.name}</h2>
              <p className="text-sm text-gray-600 mb-4">
                {competition.startDate} - {competition.endDate}
              </p>
              <form onSubmit={handleDisplayNameSubmit}>
                <Input
                  label="Display Name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Enter your name"
                  required
                  error={error}
                />
                <Button type="submit" fullWidth disabled={loading}>
                  Join Competition
                </Button>
              </form>
            </div>
          )}

          {step === 'pin' && (
            <div>
              <h2 className="text-xl font-semibold mb-4">Welcome back, {displayName}!</h2>
              <p className="text-sm text-gray-600 mb-4">
                Enter your PIN to access this competition from this device
              </p>
              <form onSubmit={handlePinSubmit}>
                <Input
                  label="PIN"
                  type="password"
                  value={pin}
                  onChange={(e) => setPin(e.target.value)}
                  placeholder="Enter your 6-digit PIN"
                  required
                  maxLength={6}
                  error={error}
                />
                <Button type="submit" fullWidth disabled={loading}>
                  Continue
                </Button>
              </form>
            </div>
          )}

          {step === 'show-pin' && (
            <div>
              <h2 className="text-xl font-semibold mb-4 text-center">Save Your PIN!</h2>
              <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-6 mb-4">
                <p className="text-sm text-gray-700 mb-2 text-center">Your PIN for this competition:</p>
                <p className="text-3xl font-bold text-center text-gray-900 tracking-widest">{generatedPin}</p>
              </div>
              <p className="text-sm text-red-600 mb-4 text-center font-semibold">
                Save this PIN! You'll need it to access this competition from other devices.
              </p>
              <p className="text-sm text-gray-600 mb-4">
                If you forget your PIN, ask the competition organizer to reset it.
              </p>
              <Button onClick={handleContinueToCompetition} fullWidth>
                Continue to Competition
              </Button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default JoinCompetition;
